import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthFetch } from '@/hooks';
import { ALLOWED_IMAGE_TYPES } from '@dermalink/shared';
import type { VerifyStatusResponse } from '@dermalink/shared';
import { useQuery } from '@tanstack/react-query';
import { getDoctorInitials, convertHeicIfNeeded, resizeImageIfNeeded } from '@/utils';
import styles from './DoctorProfile.module.css';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

// Profile pictures never render larger than the 90px hero avatar in
// ConversationThread — cap client-side to that (with headroom for high-DPI
// screens) so a phone photo doesn't upload at multi-MB full resolution.
const PROFILE_PICTURE_MAX_DIMENSION = 320;

export function DoctorProfile() {
  const authFetch   = useAuthFetch();
  const queryClient = useQueryClient();
  const fileRef     = useRef<HTMLInputElement>(null);

  const [photoVersion,   setPhotoVersion]   = useState(0);
  const [previewUrl,     setPreviewUrl]     = useState<string | null>(null);
  const [pendingS3Key,   setPendingS3Key]   = useState<string | null>(null);
  const [pendingMimeType, setPendingMimeType] = useState<string | null>(null);
  const [uploading,      setUploading]      = useState(false);
  const [isActive,       setIsActive]       = useState(true);
  const [maxParallel,    setMaxParallel]    = useState(10);
  const [saving,         setSaving]         = useState(false);
  const [saved,          setSaved]          = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flashSaved = useCallback(() => {
    setSaved(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 2500);
  }, []);

  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  const { data: status } = useQuery<VerifyStatusResponse>({
    queryKey: ['doctor-verify-status'],
    queryFn:  async () => {
      const res = await authFetch('/doctor/verify-status');
      if (!res.ok) throw new Error('Failed to load profile');
      return res.json();
    },
  });

  const { data: pictureData } = useQuery<{ picture_url: string | null }>({
    queryKey: ['doctor-picture', photoVersion],
    queryFn:  async () => {
      const res = await authFetch('/doctor/picture');
      if (!res.ok) throw new Error('Failed to load picture');
      return res.json();
    },
  });

  useEffect(() => {
    if (status) {
      setIsActive(status.is_active);
      setMaxParallel(status.max_parallel_consultations);
    }
  }, [status]);

  const initials = getDoctorInitials({ first_name: status?.first_name, last_name: status?.last_name });
  const nameLine = status ? `Dr. ${status.first_name ?? ''} ${status.last_name ?? ''}`.trim() : '—';
  const subtitle = status?.organization_name ?? status?.riziv_qualification ?? 'Dermatoloog';

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    let file = e.target.files?.[0];
    if (!fileRef.current) return;
    fileRef.current.value = '';
    if (!file) return;

    // Chrome/Firefox on Windows and Android report an empty file.type for
    // HEIC/HEIF since those OSes have no registered MIME handler for it —
    // fall back to the extension so these files aren't rejected outright.
    if (!file.type) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      const mimeType = ext === 'heic' ? 'image/heic' : ext === 'heif' ? 'image/heif' : file.type;
      if (mimeType !== file.type) file = new File([file], file.name, { type: mimeType });
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type as typeof ALLOWED_IMAGE_TYPES[number])) {
      setError('Ongeldig bestandstype. Gebruik JPEG, PNG of WebP.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('Bestand is te groot (max 5 MB).');
      return;
    }

    setError(null);
    setUploading(true);

    file = await convertHeicIfNeeded(file);
    file = await resizeImageIfNeeded(file, { maxDimension: PROFILE_PICTURE_MAX_DIMENSION, thresholdBytes: 0 });

    // Raw HEIC/HEIF can't be decoded by the browser — skip the local
    // preview and leave the current picture showing until the server-side
    // fallback conversion (during save) has produced a renderable JPEG.
    const canPreview = file.type !== 'image/heic' && file.type !== 'image/heif';
    const objectUrl  = canPreview ? URL.createObjectURL(file) : null;
    if (objectUrl) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(objectUrl);
    }

    try {
      const urlRes = await authFetch('/doctor/upload-picture-url', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ filename: file.name, mime_type: file.type }),
      });
      if (!urlRes.ok) throw new Error('Kon geen upload-URL ophalen.');
      const { url, fields, s3_key } = await urlRes.json() as { url: string; fields: Record<string, string>; s3_key: string };

      const formData = new FormData();
      for (const [k, v] of Object.entries(fields)) formData.append(k, v);
      formData.append('file', file);

      const postRes = await fetch(url, { method: 'POST', body: formData });
      if (!postRes.ok) throw new Error('Upload mislukt.');

      setPendingS3Key(s3_key);
      setPendingMimeType(file.type);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er is een fout opgetreden.');
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        setPreviewUrl(null);
      }
    } finally {
      setUploading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);

    try {
      const body: { is_active: boolean; s3_key?: string; mime_type?: string; max_parallel_consultations: number } = { is_active: isActive, max_parallel_consultations: maxParallel };
      if (pendingS3Key) {
        body.s3_key = pendingS3Key;
        body.mime_type = pendingMimeType ?? undefined;
      }

      const res = await authFetch('/doctor/profile', {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Opslaan mislukt.');

      if (pendingS3Key) {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPendingS3Key(null);
        setPendingMimeType(null);
        setPhotoVersion((v) => v + 1);
      }

      queryClient.setQueryData<VerifyStatusResponse>(['doctor-verify-status'], (old) =>
        old ? { ...old, is_active: isActive, max_parallel_consultations: maxParallel } : old,
      );

      flashSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er is een fout opgetreden.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.page}>

      <div className={styles.card}>
        <p className={styles.sectionLabel}>Voorbeeld voor patiënten</p>
        <div className={styles.doctorCardPreview}>
          <span className={styles.dcAvatar}>
            {previewUrl || pictureData?.picture_url
              ? <img src={previewUrl ?? pictureData!.picture_url!} alt="" className={styles.dcAvatarImage} />
              : initials
            }
          </span>
          <span className={styles.dcMain}>
            <span className={styles.dcName}>{nameLine}</span>
            <span className={styles.dcSub}>{subtitle}</span>
          </span>
          <span className={styles.dcArrow} aria-hidden="true">›</span>
        </div>
        <div className={styles.uploadRow}>
          <label className={styles.uploadBtn}>
            {uploading ? 'Uploaden…' : 'Foto wijzigen'}
            <input ref={fileRef} type="file" accept={ALLOWED_IMAGE_TYPES.join(',')} onChange={handleFileChange} disabled={uploading} />
          </label>
        </div>
      </div>

      <div className={styles.card}>
        <p className={styles.sectionLabel}>Beschikbaarheid</p>
        <div className={styles.availRow}>
          <div className={styles.availText}>
            <div className={styles.availTitle}>Beschikbaar voor teleconsultaties</div>
            <div className={styles.availSub}>
              {isActive
                ? 'Patiënten kunnen een nieuwe consultatie indienen.'
                : 'Patiënten kunnen geen nieuwe consultatie indienen.'}
            </div>
            <span className={`${styles.statusPill} ${isActive ? styles.statusPillActive : styles.statusPillInactive}`}>
              <span className={styles.statusDot} />
              {isActive ? 'Beschikbaar' : 'Niet beschikbaar'}
            </span>
          </div>
          <label className={styles.toggle}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span className={`${styles.toggleTrack}${isActive ? ` ${styles.toggleTrackOn}` : ''}`} />
            <span className={`${styles.toggleThumb}${isActive ? ` ${styles.toggleThumbOn}` : ''}`} />
          </label>
        </div>

        <hr className={styles.divider} />

        <div className={styles.availRow}>
          <div className={styles.availText}>
            <div className={styles.availTitle}>Maximale capaciteit</div>
            <div className={styles.availSub}>Gelijktijdige consultaties in wachtend of opvolging</div>
          </div>
          <div className={styles.stepper}>
            <button type="button" className={styles.stepBtn} onClick={() => setMaxParallel((n) => Math.max(1, n - 1))} disabled={maxParallel <= 1} aria-label="Minder">−</button>
            <span className={styles.stepValue}>{maxParallel}</span>
            <button type="button" className={styles.stepBtn} onClick={() => setMaxParallel((n) => Math.min(30, n + 1))} disabled={maxParallel >= 30} aria-label="Meer">+</button>
          </div>
        </div>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      <button className={`${styles.saveBtn}${saved ? ` ${styles.saveBtnSaved}` : ''}`} onClick={handleSave} disabled={saving || uploading}>
        {saving ? 'Opslaan…' : saved ? 'Opgeslagen' : 'Opslaan'}
      </button>

    </div>
  );
}
